import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { getNftMediaUrl } from '@/core/services/storage';
import type { Claim } from '@/core/types';

interface NftCardProps {
  claim: Claim;
  onClick: () => void;
}

export function NftCard({ claim, onClick }: NftCardProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const theme = useTheme();
  const gradient = theme.custom?.gradient || theme.palette.primary.main;

  // heraldingera: raceName, dot99: projectName
  const name = claim.raceName || (claim as { projectName?: string }).projectName || '';

  useEffect(() => {
    const fetchImage = async () => {
      try {
        if (claim.fileName) {
          const url = await getNftMediaUrl({
            fileName: claim.fileName,
            raceAlias: claim.raceAlias,
            projectId: (claim as { projectId?: string }).projectId,
          });
          setImageUrl(url);
        }
      } catch (err) {
        console.error('Failed to load NFT image:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchImage();
  }, [claim]);

  const isVideo = imageUrl?.includes('.mp4');

  return (
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        background: theme.palette.background.paper,
        border: 'none',
        borderRadius: 2,
        transition: 'transform 0.2s ease-out, box-shadow 0.2s ease-out',
        '&:hover': {
          transform: 'translateY(-4px) scale(1.02)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          padding: '2px',
          background: gradient,
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          pointerEvents: 'none',
        },
      }}
    >
      <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
        {loading ? (
          <Skeleton variant="rectangular" sx={{ aspectRatio: '1', m: 0.5, borderRadius: 1 }} />
        ) : error || !imageUrl ? (
          <Box
            sx={{
              aspectRatio: '1',
              m: 0.5,
              borderRadius: 1,
              bgcolor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography color="text.secondary">読み込み失敗</Typography>
          </Box>
        ) : isVideo ? (
          <Box sx={{ aspectRatio: '1', m: 0.5, borderRadius: 1, overflow: 'hidden' }}>
            <video
              src={imageUrl}
              muted
              loop
              autoPlay
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        ) : (
          <CardMedia component="img" image={imageUrl} alt={name} sx={{ aspectRatio: '1', m: 0.5, borderRadius: 1 }} />
        )}
        <CardContent sx={{ p: 1.5 }}>
          <Typography variant="body2" noWrap fontWeight="medium">
            {name}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
